import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Trash2, Edit, Building2, Loader2, Eye, EyeOff, MoreVertical, RefreshCw, Power, KeyRound, Wifi, WifiOff, CheckCircle2, XCircle, Globe } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import CredentialHelper, { WhatsAppSetupGuide } from '../components/shared/CredentialHelper';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// ─── Inline connection status badge ──────────────────────────────────────────
const ConnTestBadge = ({ status, message, data }) => {
  if (status === 'idle') return null;
  if (status === 'testing') return (
    <span className="inline-flex items-center gap-1.5 text-[12px] text-[#595C5C]">
      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Testing…
    </span>
  );
  if (status === 'success') return (
    <div className="mt-2 flex flex-col gap-0.5">
      <span className="inline-flex items-center gap-1.5 text-[12px] text-[#3BAB59] font-medium">
        <CheckCircle2 className="w-3.5 h-3.5" /> {message}
      </span>
      {data?.phone_number && (
        <span className="text-[11px] text-[#595C5C] pl-5">
          📱 {data.phone_number}{data.verified_name ? ` · ${data.verified_name}` : ''}{data.status ? ` · ${data.status}` : ''}
        </span>
      )}
    </div>
  );
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px] text-[#EB493D]">
      <XCircle className="w-3.5 h-3.5" /> {message}
    </span>
  );
};

const AdminClientsPage = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isResetOpen, setIsResetOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetSaving, setResetSaving] = useState(false);
  const [showResetNew, setShowResetNew] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    owner_name: '',
    owner_email: '',
    owner_password: '',
    waba_id: '',
    phone_number_id: '',
    phone_number: '',
    access_token: '',
    business_name: '',
    webhook_url: ''
  });
  const [saving, setSaving] = useState(false);

  // Connection test states
  const [waTest, setWaTest] = useState({ status: 'idle', message: '', data: null }); // idle|testing|success|error
  const [webhookTest, setWebhookTest] = useState({ status: 'idle', message: '', code: null });

  const resetConnectionTests = () => {
    setWaTest({ status: 'idle', message: '', data: null });
    setWebhookTest({ status: 'idle', message: '', code: null });
  };

  const handleTestWhatsApp = async (phoneNumberId, accessToken) => {
    if (!phoneNumberId || !accessToken) {
      toast.error('Enter Phone Number ID and Access Token first');
      return;
    }
    setWaTest({ status: 'testing', message: '', data: null });
    try {
      const res = await axios.post(`${API_URL}/clients/test-whatsapp`, {
        phone_number_id: phoneNumberId,
        access_token: accessToken
      });
      if (res.data.success) {
        setWaTest({ status: 'success', message: res.data.message, data: res.data });
      } else {
        setWaTest({ status: 'error', message: res.data.message, data: null });
      }
    } catch (e) {
      setWaTest({ status: 'error', message: e.response?.data?.detail || 'Test failed', data: null });
    }
  };

  const handleTestWebhook = async (webhookUrl) => {
    if (!webhookUrl) {
      toast.error('Enter a webhook URL first');
      return;
    }
    setWebhookTest({ status: 'testing', message: '', code: null });
    try {
      const res = await axios.post(`${API_URL}/clients/test-webhook`, { webhook_url: webhookUrl });
      setWebhookTest({
        status: res.data.success ? 'success' : 'error',
        message: res.data.message,
        code: res.data.status_code || null
      });
    } catch (e) {
      setWebhookTest({ status: 'error', message: e.response?.data?.detail || 'Test failed', code: null });
    }
  };

  const togglePasswordVisible = (clientId) => {
    setVisiblePasswords(prev => ({ ...prev, [clientId]: !prev[clientId] }));
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/clients`);
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.owner_name || !formData.owner_email || !formData.owner_password) {
      toast.error('Client name, owner name, email, and password are required');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API_URL}/clients`, formData);
      setClients([response.data, ...clients]);
      setIsCreateOpen(false);
      resetForm();
      toast.success('Client created successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedClient) return;

    setSaving(true);
    try {
      const response = await axios.put(`${API_URL}/clients/${selectedClient.id}`, {
        name: formData.name,
        waba_id: formData.waba_id || null,
        phone_number_id: formData.phone_number_id || null,
        phone_number: formData.phone_number || null,
        access_token: formData.access_token || null,
        business_name: formData.business_name || null,
        webhook_url: formData.webhook_url || null
      });
      setClients(clients.map(c => c.id === selectedClient.id ? { ...response.data, owner: selectedClient.owner } : c));
      setIsEditOpen(false);
      setSelectedClient(null);
      resetForm();
      toast.success('Client updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update client');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Are you sure? This will delete the client and all associated data.')) return;
    
    try {
      await axios.delete(`${API_URL}/clients/${clientId}`);
      setClients(clients.filter(c => c.id !== clientId));
      toast.success('Client deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete client');
    }
  };

  const handleToggleActive = async (client) => {
    try {
      await axios.put(`${API_URL}/clients/${client.id}`, { is_active: !client.is_active });
      setClients(clients.map(c => c.id === client.id ? { ...c, is_active: !c.is_active } : c));
      toast.success(`Client ${client.is_active ? 'deactivated' : 'activated'}`);
    } catch (error) {
      toast.error('Failed to update client status');
    }
  };

  const handleRegenerateToken = async (clientId) => {
    try {
      const response = await axios.post(`${API_URL}/clients/${clientId}/regenerate-token`);
      setClients(clients.map(c => c.id === clientId ? { ...c, webhook_token: response.data.webhook_token } : c));
      toast.success('Webhook token regenerated');
    } catch (error) {
      toast.error('Failed to regenerate token');
    }
  };

  const openEdit = (client) => {
    setSelectedClient(client);
    setFormData({
      name: client.name,
      owner_name: '',
      owner_email: '',
      owner_password: '',
      waba_id: client.waba_id || '',
      phone_number_id: client.phone_number_id || '',
      phone_number: client.phone_number || '',
      access_token: client.access_token || '',
      business_name: client.business_name || '',
      webhook_url: client.webhook_url || ''
    });
    resetConnectionTests();
    setIsEditOpen(true);
  };

  const openResetPassword = (client) => {
    setSelectedClient(client);
    setResetPassword('');
    setResetConfirm('');
    setShowResetNew(false);
    setIsResetOpen(true);
  };

  const handleResetPassword = async () => {
    if (!resetPassword) { toast.error('Enter a new password'); return; }
    if (resetPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (resetPassword !== resetConfirm) { toast.error('Passwords do not match'); return; }

    setResetSaving(true);
    try {
      await axios.post(`${API_URL}/clients/${selectedClient.id}/reset-password`, { new_password: resetPassword });
      // Update local state so the table immediately shows the new password
      setClients(prev => prev.map(c =>
        c.id === selectedClient.id
          ? { ...c, owner: { ...c.owner, password_plain: resetPassword } }
          : c
      ));
      setIsResetOpen(false);
      toast.success(`Password reset for ${selectedClient.owner?.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      owner_name: '',
      owner_email: '',
      owner_password: '',
      waba_id: '',
      phone_number_id: '',
      phone_number: '',
      access_token: '',
      business_name: '',
      webhook_url: ''
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-clients-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[30px] font-semibold text-[#131517]">Clients</h1>
          <p className="text-[14px] text-[#595C5C] mt-1">Manage client accounts and their WhatsApp configurations</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={(open) => { setIsCreateOpen(open); if (!open) { resetForm(); resetConnectionTests(); } }}>
          <DialogTrigger asChild>
            <Button className="bg-[#131517] hover:bg-[#333537]" data-testid="create-client-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Client
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-[20px] font-semibold text-[#131517]">Create Client</DialogTitle>
            </DialogHeader>
            <div className="space-y-6 pt-4">
              {/* Client Info */}
              <div>
                <h3 className="text-[14px] font-semibold text-[#131517] mb-3">Client Information</h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-[#131517]">Client Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Company name"
                      className="border-[#D9E2E5]"
                      data-testid="client-name-input"
                    />
                  </div>
                </div>
              </div>

              {/* Owner Info */}
              <div>
                <h3 className="text-[14px] font-semibold text-[#131517] mb-3">Owner Account</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#131517]">Owner Name *</Label>
                    <Input
                      value={formData.owner_name}
                      onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                      placeholder="Full name"
                      className="border-[#D9E2E5]"
                      data-testid="owner-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#131517]">Owner Email *</Label>
                    <Input
                      type="email"
                      value={formData.owner_email}
                      onChange={(e) => setFormData({ ...formData, owner_email: e.target.value })}
                      placeholder="email@example.com"
                      className="border-[#D9E2E5]"
                      data-testid="owner-email-input"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label className="text-[#131517]">Owner Password *</Label>
                    <Input
                      type="password"
                      value={formData.owner_password}
                      onChange={(e) => setFormData({ ...formData, owner_password: e.target.value })}
                      placeholder="Password"
                      className="border-[#D9E2E5]"
                      data-testid="owner-password-input"
                    />
                  </div>
                </div>
              </div>

              {/* WhatsApp Config */}
              <div>
                <h3 className="text-[14px] font-semibold text-[#131517] mb-3">WhatsApp Configuration (Optional)</h3>
                <WhatsAppSetupGuide />
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="space-y-2">
                    <Label className="text-[#131517] flex items-center">
                      WABA ID
                      <CredentialHelper field="waba_id" />
                    </Label>
                    <Input
                      value={formData.waba_id}
                      onChange={(e) => { setFormData({ ...formData, waba_id: e.target.value }); setWaTest({ status: 'idle', message: '', data: null }); }}
                      placeholder="WhatsApp Business Account ID"
                      className="border-[#D9E2E5]"
                      data-testid="waba-id-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#131517] flex items-center">
                      Phone Number ID
                      <CredentialHelper field="phone_number_id" />
                    </Label>
                    <Input
                      value={formData.phone_number_id}
                      onChange={(e) => { setFormData({ ...formData, phone_number_id: e.target.value }); setWaTest({ status: 'idle', message: '', data: null }); }}
                      placeholder="Phone Number ID from Meta"
                      className="border-[#D9E2E5]"
                      data-testid="phone-number-id-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#131517] flex items-center">
                      Phone Number
                      <CredentialHelper field="phone_number" />
                    </Label>
                    <Input
                      value={formData.phone_number}
                      onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                      placeholder="+1234567890"
                      className="border-[#D9E2E5]"
                      data-testid="phone-number-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#131517] flex items-center">
                      Business Name
                      <CredentialHelper field="business_name" />
                    </Label>
                    <Input
                      value={formData.business_name}
                      onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                      placeholder="Display name"
                      className="border-[#D9E2E5]"
                      data-testid="business-name-input"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-[#131517] flex items-center">
                        Access Token
                        <CredentialHelper field="access_token" />
                      </Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleTestWhatsApp(formData.phone_number_id, formData.access_token)}
                        disabled={waTest.status === 'testing' || !formData.phone_number_id || !formData.access_token}
                        className="h-7 text-[12px] border-[#D9E2E5] gap-1.5"
                        data-testid="test-wa-connection-btn"
                      >
                        {waTest.status === 'testing'
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <Wifi className="w-3 h-3" />}
                        Test Connection
                      </Button>
                    </div>
                    <Input
                      type="password"
                      value={formData.access_token}
                      onChange={(e) => { setFormData({ ...formData, access_token: e.target.value }); setWaTest({ status: 'idle', message: '', data: null }); }}
                      placeholder="Meta API Access Token"
                      className="border-[#D9E2E5]"
                      data-testid="access-token-input"
                    />
                    <ConnTestBadge status={waTest.status} message={waTest.message} data={waTest.data} />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-[#131517] flex items-center">
                        Forward Webhook URL (Optional)
                        <CredentialHelper field="webhook_url" />
                      </Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleTestWebhook(formData.webhook_url)}
                        disabled={webhookTest.status === 'testing' || !formData.webhook_url}
                        className="h-7 text-[12px] border-[#D9E2E5] gap-1.5"
                        data-testid="test-webhook-btn"
                      >
                        {webhookTest.status === 'testing'
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <Globe className="w-3 h-3" />}
                        Test Webhook
                      </Button>
                    </div>
                    <Input
                      value={formData.webhook_url}
                      onChange={(e) => { setFormData({ ...formData, webhook_url: e.target.value }); setWebhookTest({ status: 'idle', message: '', code: null }); }}
                      placeholder="https://client-server.com/webhook"
                      className="border-[#D9E2E5]"
                      data-testid="webhook-url-input"
                    />
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] text-[#8A8A8B]">Messages will be forwarded to this URL</p>
                      <ConnTestBadge status={webhookTest.status} message={webhookTest.message} data={null} />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-[#D9E2E5]">
                <Button variant="outline" onClick={() => setIsCreateOpen(false)} className="border-[#D9E2E5]">
                  Cancel
                </Button>
                <Button onClick={handleCreate} disabled={saving} className="bg-[#131517] hover:bg-[#333537]" data-testid="save-client-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Create Client
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Clients Table */}
      <Card className="border-[#D9E2E5]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#595C5C]" />
            </div>
          ) : clients.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="border-[#D9E2E5] hover:bg-transparent">
                  <TableHead className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Client</TableHead>
                  <TableHead className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Owner</TableHead>
                  <TableHead className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Password</TableHead>
                  <TableHead className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Phone</TableHead>
                  <TableHead className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Status</TableHead>
                  <TableHead className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Created</TableHead>
                  <TableHead className="text-[12px] font-semibold text-[#8A8A8B] uppercase text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clients.map((client) => (
                  <TableRow key={client.id} className="border-[#D9E2E5]" data-testid={`client-row-${client.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-[#EFEFEF] rounded-lg flex items-center justify-center">
                          <Building2 className="w-4 h-4 text-[#595C5C]" />
                        </div>
                        <div>
                          <p className="font-medium text-[#131517]">{client.name}</p>
                          {client.business_name && (
                            <p className="text-[11px] text-[#8A8A8B]">{client.business_name}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-[#131517]">{client.owner?.name}</p>
                      <p className="text-[11px] text-[#8A8A8B]">{client.owner?.email}</p>
                    </TableCell>
                    <TableCell>
                      {client.owner?.password_plain ? (
                        <div className="flex items-center gap-1.5">
                          <code className="text-[12px] font-mono text-[#131517] bg-[#F8F9FA] border border-[#D9E2E5] px-2 py-0.5 rounded">
                            {visiblePasswords[client.id] ? client.owner.password_plain : '••••••••'}
                          </code>
                          <button
                            onClick={() => togglePasswordVisible(client.id)}
                            className="text-[#8A8A8B] hover:text-[#595C5C] transition-colors flex-shrink-0"
                            title={visiblePasswords[client.id] ? 'Hide password' : 'Show password'}
                            data-testid={`toggle-password-${client.id}`}
                          >
                            {visiblePasswords[client.id]
                              ? <EyeOff className="w-3.5 h-3.5" />
                              : <Eye className="w-3.5 h-3.5" />
                            }
                          </button>
                        </div>
                      ) : (
                        <span className="text-[11px] text-[#AFB0B0] italic">Not available</span>
                      )}
                    </TableCell>
                    <TableCell className="text-[#595C5C]">
                      {client.phone_number || <span className="text-[#AFB0B0]">Not set</span>}
                    </TableCell>
                    <TableCell>
                      {client.is_active !== false ? (
                        <span className="badge-success">Active</span>
                      ) : (
                        <span className="badge-error">Inactive</span>
                      )}
                    </TableCell>
                    <TableCell className="text-[#595C5C]">
                      {new Date(client.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`client-menu-${client.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(client)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openResetPassword(client)}>
                            <KeyRound className="w-4 h-4 mr-2" />
                            Reset Password
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggleActive(client)}>
                            <Power className="w-4 h-4 mr-2" />
                            {client.is_active !== false ? 'Deactivate' : 'Activate'}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleRegenerateToken(client.id)}>
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Regenerate Token
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleDelete(client.id)} className="text-[#EB493D]">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 mx-auto text-[#D9E2E5] mb-3" />
              <p className="text-[14px] text-[#8A8A8B]">No clients yet</p>
              <p className="text-[12px] text-[#AFB0B0] mt-1">Create your first client to get started</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[20px] font-semibold text-[#131517]">Edit Client</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pt-4">
            <div className="space-y-2">
              <Label className="text-[#131517]">Client Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="border-[#D9E2E5]"
              />
            </div>

            <div>
              <h3 className="text-[14px] font-semibold text-[#131517] mb-3">WhatsApp Configuration</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#131517]">WABA ID</Label>
                  <Input
                    value={formData.waba_id}
                    onChange={(e) => setFormData({ ...formData, waba_id: e.target.value })}
                    className="border-[#D9E2E5]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#131517]">Phone Number ID</Label>
                  <Input
                    value={formData.phone_number_id}
                    onChange={(e) => { setFormData({ ...formData, phone_number_id: e.target.value }); setWaTest({ status: 'idle', message: '', data: null }); }}
                    className="border-[#D9E2E5]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#131517]">Phone Number</Label>
                  <Input
                    value={formData.phone_number}
                    onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                    className="border-[#D9E2E5]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#131517]">Business Name</Label>
                  <Input
                    value={formData.business_name}
                    onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                    className="border-[#D9E2E5]"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-[#131517]">Access Token</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleTestWhatsApp(formData.phone_number_id, formData.access_token)}
                      disabled={waTest.status === 'testing' || !formData.phone_number_id || !formData.access_token}
                      className="h-7 text-[12px] border-[#D9E2E5] gap-1.5"
                      data-testid="edit-test-wa-btn"
                    >
                      {waTest.status === 'testing'
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <Wifi className="w-3 h-3" />}
                      Test Connection
                    </Button>
                  </div>
                  <Input
                    type="password"
                    value={formData.access_token}
                    onChange={(e) => { setFormData({ ...formData, access_token: e.target.value }); setWaTest({ status: 'idle', message: '', data: null }); }}
                    placeholder="Leave blank to keep current"
                    className="border-[#D9E2E5]"
                  />
                  <ConnTestBadge status={waTest.status} message={waTest.message} data={waTest.data} />
                </div>
                <div className="col-span-2 space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-[#131517]">Forward Webhook URL</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleTestWebhook(formData.webhook_url)}
                      disabled={webhookTest.status === 'testing' || !formData.webhook_url}
                      className="h-7 text-[12px] border-[#D9E2E5] gap-1.5"
                      data-testid="edit-test-webhook-btn"
                    >
                      {webhookTest.status === 'testing'
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <Globe className="w-3 h-3" />}
                      Test Webhook
                    </Button>
                  </div>
                  <Input
                    value={formData.webhook_url}
                    onChange={(e) => { setFormData({ ...formData, webhook_url: e.target.value }); setWebhookTest({ status: 'idle', message: '', code: null }); }}
                    className="border-[#D9E2E5]"
                  />
                  <ConnTestBadge status={webhookTest.status} message={webhookTest.message} data={null} />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-[#D9E2E5]">
              <Button variant="outline" onClick={() => setIsEditOpen(false)} className="border-[#D9E2E5]">
                Cancel
              </Button>
              <Button onClick={handleUpdate} disabled={saving} className="bg-[#131517] hover:bg-[#333537]">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Reset Password Dialog */}
      <Dialog open={isResetOpen} onOpenChange={setIsResetOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[18px] font-semibold text-[#131517]">Reset Password</DialogTitle>
            <DialogDescription className="text-[13px] text-[#595C5C]">
              Set a new password for <span className="font-medium text-[#131517]">{selectedClient?.owner?.email}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label className="text-[#131517] text-[13px]">New Password</Label>
              <div className="relative">
                <Input
                  type={showResetNew ? 'text' : 'password'}
                  value={resetPassword}
                  onChange={e => setResetPassword(e.target.value)}
                  placeholder="Min. 6 characters"
                  className="border-[#D9E2E5] pr-10"
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowResetNew(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8A8B] hover:text-[#595C5C]"
                >
                  {showResetNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#131517] text-[13px]">Confirm Password</Label>
              <Input
                type="password"
                value={resetConfirm}
                onChange={e => setResetConfirm(e.target.value)}
                placeholder="Re-enter new password"
                className="border-[#D9E2E5]"
                onKeyDown={e => e.key === 'Enter' && handleResetPassword()}
                data-testid="reset-confirm-input"
              />
              {resetConfirm && resetPassword !== resetConfirm && (
                <p className="text-[11px] text-[#EB493D]">Passwords do not match</p>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-[#D9E2E5]">
              <Button variant="outline" onClick={() => setIsResetOpen(false)} className="border-[#D9E2E5]">
                Cancel
              </Button>
              <Button
                onClick={handleResetPassword}
                disabled={resetSaving || !resetPassword || resetPassword !== resetConfirm}
                className="bg-[#131517] hover:bg-[#333537]"
                data-testid="confirm-reset-btn"
              >
                {resetSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
                Reset Password
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminClientsPage;
